import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { rem, space, hairline, media, breakpoint } from '@gbi/ui';

import tokens from '../../assets/styles/tokens';

// TODO: placeholder figures until live spot prices are wired in. Do not ship
// these to production as-is.
const SPOT_PRICES = [
  { key: 'gold', price: '$4,920.23/oz', change: '0.12%', isUp: false },
  { key: 'silver', price: '$76.05/oz', change: '0.12%', isUp: true },
  { key: 'goldSilverRatio', price: '$57.54/oz', change: '0.12%', isUp: false },
];

const BAR_HEIGHT = 77;
const ITEM_HEIGHT = 20;
const ARROW_HALF_WIDTH = 7;
const ARROW_HEIGHT = 8;

// Figma "Rectangle 40027" values that have no role in @gbi/ui yet.
const BAR_BACKGROUND = '#F9F9F9'; // not in @gbi/ui yet
const BAR_BORDER = 'rgba(0, 0, 0, 0.2)'; // not in @gbi/ui yet
const LOSS_COLOR = '#E01A0E'; // not in @gbi/ui yet

// Full-bleed band directly under the header: content inset 24px from each
// edge, 77px tall on desktop.
const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${rem(space[12])} ${rem(space[24])};
  box-sizing: border-box;
  width: 100%;
  min-height: ${rem(BAR_HEIGHT)};
  padding: 0 ${rem(space[24])};
  background-color: ${BAR_BACKGROUND};
  border-top: ${hairline} solid ${BAR_BORDER};
  border-bottom: ${hairline} solid ${BAR_BORDER};

  ${media.below(breakpoint.xl)} {
    min-height: 0;
    padding: ${rem(space[12])} ${rem(space[16])};
  }
`;

const PriceList = styled.ul`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;

  ${media.below(breakpoint.xl)} {
    gap: ${rem(space[8])} ${rem(space[16])};
  }
`;

const PriceItem = styled.li`
  ${tokens.type.body[300]}
  display: inline-flex;
  align-items: center;
  gap: ${rem(space[12])};
  min-height: ${rem(ITEM_HEIGHT)};
  padding: 0 ${rem(space[12])};
  color: ${tokens.color.text.primary};
  white-space: nowrap;

  &:first-child {
    padding-left: 0;
  }

  & + & {
    border-left: ${hairline} solid ${BAR_BORDER};
  }

  /* Narrow screens: the items may wrap onto several rows, so no dividers
     (a divider would dangle at the start of a wrapped row) and the item
     itself may wrap rather than overflow a 320px screen. */
  ${media.below(breakpoint.xl)} {
    flex-wrap: wrap;
    gap: ${rem(space[2])} ${rem(space[8])};
    padding: 0;
    white-space: normal;

    & + & {
      border-left: none;
    }
  }
`;

const Label = styled.span`
  ${tokens.type.title[300]}
  margin-right: ${rem(space[4])};
`;

const Change = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(space[4])};
  color: ${({ $isUp }) => ($isUp ? tokens.color.status.success : LOSS_COLOR)};
`;

// CSS triangle (no asset): points up for a gain, down for a loss.
const Arrow = styled.span`
  width: 0;
  height: 0;
  border-left: ${rem(ARROW_HALF_WIDTH)} solid transparent;
  border-right: ${rem(ARROW_HALF_WIDTH)} solid transparent;
  ${({ $isUp }) =>
    $isUp
      ? `border-bottom: ${rem(ARROW_HEIGHT)} solid currentColor;`
      : `border-top: ${rem(ARROW_HEIGHT)} solid currentColor;`}
`;

// Direction is shown by colour and the triangle only, so spell it out for
// screen readers ("Down 0.12%").
const VisuallyHidden = styled.span`
  position: absolute;
  width: ${hairline};
  height: ${hairline};
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Action = styled.div`
  margin-left: auto;
`;

// The strip under the header: spot prices on the left, an optional action
// (the "Buy at Target Price" CTA) pinned to the right on the same row.
const SpotPricesBar = ({ showPrices, action }) => {
  const { t } = useTranslation();

  if (!showPrices && !action) {
    return null;
  }

  return (
    <Bar data-cy="container-spot-prices">
      {showPrices && (
        <PriceList>
          {SPOT_PRICES.map(({ key, price, change, isUp }) => (
            <PriceItem key={key} data-cy={`spot-price-${key}`}>
              <Label>{t(`spotPrices.${key}`)}</Label>
              <span>{price}</span>
              <Change $isUp={isUp}>
                <Arrow $isUp={isUp} aria-hidden="true" />
                <VisuallyHidden>
                  {t(isUp ? 'spotPrices.up' : 'spotPrices.down')}
                </VisuallyHidden>
                {change}
              </Change>
            </PriceItem>
          ))}
        </PriceList>
      )}
      {action && <Action>{action}</Action>}
    </Bar>
  );
};

SpotPricesBar.propTypes = {
  showPrices: PropTypes.bool,
  action: PropTypes.node,
};

SpotPricesBar.defaultProps = {
  showPrices: false,
  action: null,
};

export default SpotPricesBar;
